import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { colors } from '@federa/components/css-refs.colors';
import { divider, proportion } from '@federa/components/css-refs.layout';
import { typography } from '@federa/components/css-refs.typography';
import { spacing } from '@federa/components/css-refs.spacing';
import { motion, borderRadius, boxShadow } from '@federa/components/css-refs.effects';
import { MONO_FONT_STYLE, TEXT_SM_SEMIBOLD_SPACED } from '@federa/components/common/layout-styles';
import { createBackgroundHoverHandlers, createHoverDeltaHandlers } from '@federa/components/common/event-handlers';
import { normalizeString } from '@federa/const/string-normalize';
import { isNonEmptyString } from '@federa/const/string-guards';
import { IMAGE_MEDIA_TYPE_PREFIX } from '@federa/const/files';
import { VStack } from '@federa/components/layout/Stack';
import { FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { extractInlineCards } from '@federa/utils/conversations/inline-card-extract';
import { INLINE_FLEX_ROW_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { REL_NOOPENER_NOREFERRER } from '@federa/const/html-link-rel';
import { BORDER_MUTED } from '@federa/components/common/style-primitives';
import { InlineFunctionCard, InlinePropertyCard } from './AssistantPane.components.inline-cards.tsx';
import type { CSSProperties } from 'react';
import type { ChatMessageStruct } from '@federa/types/conversations-chat-structs';

const ATTACHMENT_IMAGE_CONTENT_STYLE: CSSProperties = {
	...FULL_WIDTH_STYLE,
	height: 'auto',
	display: 'block',
};
const FILE_ATTACHMENT_LABEL_STYLE: CSSProperties = { fontSize: typography.fontSize.lg };
const ATTACHMENTS_STACK_STYLE: CSSProperties = { marginTop: spacing[3] };
const MARKDOWN_PARAGRAPH_STYLE: CSSProperties = {
	margin: 0,
	padding: 0,
	whiteSpace: 'pre-wrap',
	wordBreak: 'break-word',
};

// AUD-006: Design constant for attachment image max width
const ATTACHMENT_IMAGE_MAX_WIDTH = `calc(${spacing[50]} + ${spacing[25]})`;

/** The heading depths this pane's markdown renderer styles. The union is a DECISION of this leaf -- `HEADING_CONFIG`
 *  declares a row per depth and both builders index it -- so a fourth depth is added in ONE place and the table, the
 *  style builder and the component factory cannot disagree about what they accept. */
type MarkdownHeadingLevel = 'h1' | 'h2' | 'h3';

const HEADING_CONFIG: Record<MarkdownHeadingLevel, React.CSSProperties> = {
	h1: {
		fontSize: typography.fontSize.lg,
		fontWeight: typography.fontWeight.bold,
		marginBottom: spacing[2],
	},
	h2: {
		fontSize: typography.fontSize.base,
		fontWeight: typography.fontWeight.semibold,
		marginBottom: spacing[1],
	},
	h3: TEXT_SM_SEMIBOLD_SPACED,
};

const LIST_STYLE = {
	marginLeft: spacing[4],
	marginTop: spacing[2],
	marginBottom: spacing[2],
};

function buildHeadingStyle(level: MarkdownHeadingLevel, isOwn: boolean): CSSProperties {
	const config = HEADING_CONFIG[level];
	return {
		fontSize: config.fontSize,
		fontWeight: config.fontWeight,
		marginTop: spacing[2],
		marginBottom: config.marginBottom,
		color: isOwn ? colors.background.primary : colors.text.primary,
	};
}

function buildImageAttachmentAnchorStyle(isOwn: boolean): CSSProperties {
	return {
		display: 'block',
		maxWidth: ATTACHMENT_IMAGE_MAX_WIDTH,
		borderRadius: spacing[2],
		overflow: 'hidden',
		border: `${divider.thickness.thin} solid ${isOwn ? colors.overlay.mediumWhite : colors.border.muted}`,
		transition: `opacity ${motion.duration.medium}`,
	};
}

function buildFileAttachmentStyle(isOwn: boolean): CSSProperties {
	return {
		...INLINE_FLEX_ROW_CENTER_STYLE,
		gap: spacing[2],
		padding: `${spacing[2]} ${spacing[3]}`,
		backgroundColor: isOwn ? colors.overlay.subtle : colors.background.tertiary,
		color: isOwn ? colors.background.primary : colors.brand.primary,
		borderRadius: spacing[1.5],
		fontSize: typography.fontSize.sm,
		textDecoration: 'none',
		transition: `background-color ${motion.duration.medium}`,
		border: `${divider.thickness.thin} solid ${isOwn ? colors.overlay.mediumWhite : colors.border.muted}`,
	};
}

function buildPreCodeStyle(isOwn: boolean): CSSProperties {
	return {
		backgroundColor: isOwn ? colors.overlay.code : colors.background.coolNeutral,
		borderRadius: spacing[2],
		padding: spacing[3],
		marginTop: spacing[2],
		marginBottom: spacing[2],
		overflowX: 'auto',
		maxWidth: '100%',
	};
}

function buildLinkStyle(isOwn: boolean): CSSProperties {
	return {
		color: isOwn ? colors.background.primary : colors.brand.primary,
		textDecoration: 'underline',
		fontWeight: typography.fontWeight.medium,
	};
}

function buildBubbleStyle(isOwn: boolean): CSSProperties {
	return {
		borderRadius: borderRadius.xl,
		maxWidth: proportion.bubble,
		minWidth: spacing[25],
		padding: `${spacing[3]} ${spacing[4]}`,
		backgroundColor: isOwn ? colors.brand.primary : colors.background.primary,
		color: isOwn ? colors.background.primary : colors.text.primary,
		fontSize: typography.fontSize.sm,
		lineHeight: typography.lineHeight.normal,
		boxShadow: isOwn ? 'none' : boxShadow.xs,
		border: isOwn ? 'none' : BORDER_MUTED,
		wordBreak: 'break-word',
		overflowWrap: 'break-word',
	};
}

function styledHeading(level: MarkdownHeadingLevel, isOwn: boolean) {
	const Tag = level;
	const HeadingComponent = ({ children, ...props }: React.ComponentPropsWithoutRef<typeof Tag>) => (
		<Tag style={buildHeadingStyle(level, isOwn)} {...props}>
			{children}
		</Tag>
	);
	HeadingComponent.displayName = `StyledHeading${level.toUpperCase()}`;
	return HeadingComponent;
}

interface ImageAttachmentProps {
	url: string;
	name?: string;
	isOwn: boolean;
}

const IMAGE_ATTACHMENT_HOVER_HANDLERS = createHoverDeltaHandlers({ opacity: { enter: '0.8', leave: '1' } });

function ImageAttachment({ url, name, isOwn }: ImageAttachmentProps) {
	return (
		<a
			href={url}
			target="_blank"
			rel={REL_NOOPENER_NOREFERRER}
			style={buildImageAttachmentAnchorStyle(isOwn)}
			{...IMAGE_ATTACHMENT_HOVER_HANDLERS}
		>
			{/* Plain img: the Vite-built SPA has no next/image runtime. */}
			<img src={url} alt={name ?? 'Attachment'} width={300} height={200} style={ATTACHMENT_IMAGE_CONTENT_STYLE} loading="lazy" />
		</a>
	);
}

function FileAttachment({ url, name, isOwn }: { url: string; name?: string; isOwn: boolean }) {
	return (
		<a
			href={url}
			target="_blank"
			rel={REL_NOOPENER_NOREFERRER}
			style={buildFileAttachmentStyle(isOwn)}
			{...createBackgroundHoverHandlers({
				hoverColor: isOwn ? colors.overlay.mediumWhite : colors.border.muted,
				defaultColor: isOwn ? colors.overlay.subtle : colors.background.tertiary,
			})}
		>
			<span style={FILE_ATTACHMENT_LABEL_STYLE}>[FILE]</span>
			<span>{name && name !== 'null' ? name : 'Download file'}</span>
		</a>
	);
}

function AttachmentItem({ url, name, type, isOwn }: { url: string; name?: string; type?: string; isOwn: boolean }) {
	const isImage = (type?.startsWith(IMAGE_MEDIA_TYPE_PREFIX) ?? false) || (url ? /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url) : false);

	if (isImage) {
		return <ImageAttachment url={url} name={name} isOwn={isOwn} />;
	}
	return <FileAttachment url={url} name={name} isOwn={isOwn} />;
}

function MessageAttachments({
	attachments,
	isOwn,
}: {
	attachments: Array<{ url?: string; name?: string; type?: string }>;
	isOwn: boolean;
}) {
	const validAttachments = attachments.filter((a) => isNonEmptyString(a.url) && a.url !== 'null');

	if (validAttachments.length === 0) return null;

	return (
		<VStack gap={spacing[2]} style={ATTACHMENTS_STACK_STYLE}>
			{validAttachments.map((attachment, idx) => (
				<div key={idx}>
					<AttachmentItem url={attachment.url ?? ''} name={attachment.name} type={attachment.type} isOwn={isOwn} />
				</div>
			))}
		</VStack>
	);
}

function getInlineCodeStyle(isOwn: boolean) {
	return {
		backgroundColor: isOwn ? colors.overlay.subtle : colors.background.tertiary,
		color: isOwn ? colors.background.primary : colors.text.primary,
		padding: `${spacing[0.5]} ${spacing[1.5]}`,
		borderRadius: spacing[1],
		fontSize: typography.fontSize.xs,
		...MONO_FONT_STYLE,
	};
}

function getCodeBlockStyle(isOwn: boolean) {
	return {
		fontSize: typography.fontSize.xs,
		...MONO_FONT_STYLE,
		color: isOwn ? colors.background.primary : colors.text.primary,
	};
}

function createCodeComponent(isOwn: boolean) {
	const CodeComponent = ({ className, children, ...props }: React.ComponentPropsWithoutRef<'code'>) => {
		const isInline = !className?.includes('language-');
		if (isInline) {
			return (
				<code style={getInlineCodeStyle(isOwn)} {...props}>
					{children}
				</code>
			);
		}
		return (
			<pre style={buildPreCodeStyle(isOwn)}>
				<code className={className} style={getCodeBlockStyle(isOwn)} {...props}>
					{children}
				</code>
			</pre>
		);
	};
	CodeComponent.displayName = 'CodeComponent';
	return CodeComponent;
}

function createMarkdownComponents(isOwn: boolean) {
	return {
		code: createCodeComponent(isOwn),
		a: ({ children, ...props }: React.ComponentPropsWithoutRef<'a'>) => (
			<a style={buildLinkStyle(isOwn)} target="_blank" rel={REL_NOOPENER_NOREFERRER} {...props}>
				{children}
			</a>
		),
		ul: (props: React.ComponentPropsWithoutRef<'ul'>) => <ul style={LIST_STYLE} {...props} />,
		ol: (props: React.ComponentPropsWithoutRef<'ol'>) => <ol style={LIST_STYLE} {...props} />,
		p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
			<p style={MARKDOWN_PARAGRAPH_STYLE} {...props}>
				{children}
			</p>
		),
		h1: styledHeading('h1', isOwn),
		h2: styledHeading('h2', isOwn),
		h3: styledHeading('h3', isOwn),
	};
}

interface MessageProps {
	message: ChatMessageStruct;
	username: string;
	userId: string;
}

/**
 * Is this message the viewer's own?
 *
 * ID FIRST, then name, and only false when neither matches. The id is the
 * authoritative identity; the name check exists for rows that carry a display
 * name but no resolvable sender id.
 *
 * The order used to be reversed, and the name branch RETURNED rather than fell
 * through -- so whenever a viewer had a username and the row carried any
 * different senderName, the id was never consulted and the message rendered as
 * someone else's. Guests hit this every time: the server echoes their own
 * messages back with senderName "Unknown", which matches no username, so a
 * guest's own turns appeared left-aligned and labelled "Unknown" beside Alex's.
 */
function computeIsOwn(message: ChatMessageStruct, username: string, userId: string): boolean {
	if (userId && message.senderId && message.senderId === userId) return true;
	if (username && message.senderName) {
		return normalizeString(message.senderName) === normalizeString(username);
	}
	return false;
}

function computeSenderLabel(message: ChatMessageStruct, isOwn: boolean): string | null {
	if (isOwn) return null;
	return message.senderName?.trim() || 'Alex';
}

interface MessageBubbleProps {
	content: string;
	isOwn: boolean;
	message: ChatMessageStruct;
	extracted: ReturnType<typeof extractInlineCards> | null;
}

function MessageBubble({ content, isOwn, message, extracted }: MessageBubbleProps) {
	return (
		<div style={buildBubbleStyle(isOwn)}>
			<ReactMarkdown remarkPlugins={[remarkGfm]} components={createMarkdownComponents(isOwn)}>
				{content}
			</ReactMarkdown>
			{message.attachments && <MessageAttachments attachments={message.attachments} isOwn={isOwn} />}
			{extracted?.functionExecutionId && <InlineFunctionCard executionId={extracted.functionExecutionId} />}
			{extracted?.propertyId && <InlinePropertyCard propertyId={extracted.propertyId} />}
		</div>
	);
}

// AUD-007: Message component with isOwn calculated locally for effective memoization
const Message = memo(({ message, username, userId }: MessageProps) => {
	const isOwn = computeIsOwn(message, username, userId);
	const extracted = isOwn ? null : extractInlineCards(message.content);
	const renderedContent = extracted ? extracted.cleanedText : message.content;
	const senderLabel = computeSenderLabel(message, isOwn);

	return (
		<div
			className="flex"
			style={{
				flexDirection: 'column',
				alignItems: isOwn ? 'flex-end' : 'flex-start',
				maxWidth: '100%',
				gap: spacing[1],
			}}
		>
			<MessageBubble content={renderedContent} isOwn={isOwn} message={message} extracted={extracted} />
			{senderLabel && (
				<span
					style={{
						fontSize: typography.fontSize.xs,
						color: colors.text.tertiary,
						paddingLeft: spacing[2],
					}}
				>
					{senderLabel}
				</span>
			)}
		</div>
	);
});

Message.displayName = 'Message';

interface MessageListProps {
	messages: ChatMessageStruct[];
	username: string;
	userId: string;
}

export const MessageList = memo(({ messages, username, userId }: MessageListProps) => {
	return (
		<VStack gap={spacing[4]}>
			{messages.map((message) => (
				<Message key={message.id} message={message} username={username} userId={userId} />
			))}
		</VStack>
	);
});

MessageList.displayName = 'MessageList';
